import React, { useEffect, useState } from 'react';
import './styles';
import { useSelector } from 'react-redux';
import { EDIT_TYPE_SERVICE, FILE_STATUS_OPTIONS, FILE_TYPE_RAW_IMAGE, SERVICE_REQUESTS } from '../../../../utils/Config/Constants';
import UIIcon from '../../../../components/UI/UIIcon';
import { CameraWhite, DownloadBlack, DownloadIcon, EditPhotoIcon, SettingsWhite, StageIcon, Trashcan } from '../../../../assets/icons';
import UIHover from '../../../../components/UI/UIHover/UIHover';
import UIModal from '../../../../components/UI/UIModal';
import Queries from '../../../../utils/API/Queries';
import UILabeledInput from '../../../../components/UI/UILabeledInput';
import UILoadingPage from '../../../../components/UI/UILoadingPage/UILoadingPage';
import UIIconButton from '../../../../components/UI/UIIconButton';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { ZendeskAPI } from 'react-zendesk';
import { ContextMenu, ContextMenuTrigger } from 'react-contextmenu';
import UIOverlayCard from '../../../../components/UI/UIOverlayCard';
import UIMenuBody, { filterSelectedItem } from '../../../../components/UI/UIMenuBody';
import { SERVICE_REQUEST_EDITOR, SERVICE_REQUEST_FIXED_FILES, SERVICE_REQUEST_INCORRECT, SERVICE_REQUEST_NOTES } from '../../../../utils/Config/MenuBodyTabs';
import { generateLocationFromProperty } from '../../../../utils/Helpers/ServiceHelper';
import { getReadableStatus } from '../../../../utils/Helpers/PaymentHelper/PaymentHelper';
import { UI_LABELED_INPUT_DROPDOWN_TYPE } from '../../../../components/UI/UILabeledInput/config';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/react-hooks';
import { getIsUserAdminSelector, getSignedInUserSelector } from '../../../../redux/selectors/CurrentUserSelectors';
import { toast } from 'react-toastify';
import ServiceRequestNotesTab from './ServiceRequestNotesTab';
import ServiceRequestEditorTab from './ServiceRequestEditorTab';
import ServiceRequestIncorrectTab from './ServiceRequestIncorrectTab';
import ServiceRequestFixedFilesTab from './ServiceRequestFixedFilesTab';
import { GET_INDIVIDUAL_SERVICE_REQUEST, GET_INDIVIDUAL_SERVICE_REQUEST_PROPERTY } from './queries';
import * as strings from './strings';
import { DropdownItem } from '../../../../components/UI/UILabeledInput/types';
import { getApiUrl } from '../../../../utils/API/APIConfig';
import { getInitialRoute } from '../../../../utils/Helpers/UserHelpers';
import { UI_ICON_BUTTON_CANCEL_TYPE, UI_ICON_BUTTON_OUTLINE_TYPE } from '../../../../components/UI/UIIconButton/config';

export default function IndividualServiceRequest({}) {
	let isUserAdmin = useSelector(getIsUserAdminSelector);
	let signedInUser = useSelector(getSignedInUserSelector);

	const client = useApolloClient();
	const match = useRouteMatch();
	const history = useHistory();
	const location = useLocation();

	const [serviceRequest, setServiceRequest] = useState<ServiceRequestType>();
	const [isRemovingServiceRequest, setIsRemovingServiceRequest] = useState<boolean>();
	const [completedFileStatus, setCompletedFileStatus] = useState<DropdownItem[]>([]);
	const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
	const [isSendingForApproval, setIsSendingForApproval] = useState<boolean>(false);
	const [isApproving, setIsApproving] = useState<boolean>(false);
	const [isSendingToEditor, setIsSendingToEditor] = useState<boolean>(false);
	const [showApproveServiceRequestModal, setShowApproveServiceRequestModal] = useState<boolean>(false);
	const [showRemoveRequestModal, setShowRemoveRequestModal] = useState<boolean>(false);
	const [showRequestStatusModal, setShowRequestStatusModal] = useState<boolean>(false);
	const [serviceRequestProperty, setServiceRequestProperty] = useState<PropertyType>();

	const [updateServiceRequestCall] = useMutation(Queries.UPDATE_SERVICE_REQUEST);

	const [getServiceRequestProperty, { data: serviceRequestPropertyData }] = useLazyQuery(GET_INDIVIDUAL_SERVICE_REQUEST_PROPERTY);

	const [getServiceRequests, { data: serviceRequestData }] = useLazyQuery(GET_INDIVIDUAL_SERVICE_REQUEST, {
		variables: { id: match.params['id'] }
	});

	// Mark: Lifecycle functions

	useEffect(() => {
		(async () => {
			await getServiceRequests();
		})();
	}, []);

	useEffect(() => {
		(async () => {
			if (serviceRequestData) {
				setServiceRequest(serviceRequestData.serviceRequest);
				let variables = { id: serviceRequestData.serviceRequest.service.property.id };
				await getServiceRequestProperty({ variables });
			}
		})();
	}, [serviceRequestData]);

	useEffect(() => {
		(async () => {
			if (serviceRequestPropertyData) {
				setServiceRequestProperty(serviceRequestPropertyData.property);
			}
		})();
	}, [serviceRequestPropertyData]);

	if (!serviceRequest) return null;
	//Mark: Component functions

	const approveServiceRequest = async () => {
		if (!isApproving) {
			setIsApproving(true);
			const response = await client.mutate({
				mutation: Queries.APPROVE_SERVICE_REQUEST,
				variables: { id: serviceRequest.id, completedFileStatus: completedFileStatus }
			});
			const { approveServiceRequest: approvalResponse } = response['data'];
			if (approvalResponse) {
				if (serviceRequest.service) {
					const serviceRef = serviceRequest.service.id;
					let variables = { title: 'imageServiceRequestComplete', service: serviceRef, serviceRequest: serviceRequest.id };
					await client.mutate({ mutation: Queries.CREATE_ACTION, variables }).catch(() => {});
					getServiceRequests();
					setShowApproveServiceRequestModal(false);
					setIsApproving(false);
					toast.success(strings.approveServiceRequestSuccess);
				}
			}
		}
	};

	const sendForApproval = async () => {
		if (!isSendingForApproval) {
			setIsSendingForApproval(true);
			let variables = { id: serviceRequest.id, status: SERVICE_REQUESTS.status.awaitingAdminApproval };

			const response = await updateServiceRequestCall({ variables });

			const { updateServiceRequest } = response['data'];
			if (updateServiceRequest) {
				toast.success(strings.sentForReviewSuccess);
			}
			await getServiceRequests();
			setIsSendingForApproval(false);
		}
	};

	const sendServiceRequestToEditor = async () => {
		if (!isSendingToEditor) {
			setIsSendingToEditor(true);
			let variables = { id: serviceRequest.id, status: SERVICE_REQUESTS.status.inProgress };
			const response = await updateServiceRequestCall({ variables });
			const { updateServiceRequest } = response['data'];
			if (updateServiceRequest) {
				toast.success(strings.sentToEditorSuccess);
			}
			await getServiceRequests();
			setIsSendingToEditor(false);
		}
	};

	const getTabs = () => {
		return [SERVICE_REQUEST_NOTES, SERVICE_REQUEST_EDITOR, SERVICE_REQUEST_FIXED_FILES, SERVICE_REQUEST_INCORRECT];
	};

	const serviceRequestStatusChange = async (status) => {
		let variables = { id: serviceRequest.id, status: status };
		const response = await updateServiceRequestCall({ variables });
		let updatedStatus = response['data'];
		if (updatedStatus) {
			await getServiceRequests();
			setShowRequestStatusModal(false);
			setShowRequestStatusModal(false);
			toast.success(strings.statusChangeSuccess);
		}
	};

	const deleteServiceRequest = async () => {
		setIsRemovingServiceRequest(true);
		const response = await client.mutate({
			mutation: Queries.UPDATE_SERVICE_REQUEST,
			variables: {
				id: serviceRequest.id,
				isActive: false
			}
		});
		setIsRemovingServiceRequest(false);
		const { updateServiceRequest } = response['data'];
		if (updateServiceRequest) {
			await getServiceRequests();
			setShowRemoveRequestModal(false);
			if (isUserAdmin && serviceRequest.service) {
				history.push(getInitialRoute(signedInUser) + `?service=${serviceRequest.service.id}`);
			} else {
				//@ts-ignore
				history.goBack();
			}
			toast.success(strings.removedSuccess);
		}
	};

	const onClickSidebarService = (service) => {
		history.push(location.pathname + `?service=${service.id}`);
	};

	const renderAddress = () => {
		if (!serviceRequestProperty) {
			return;
		} else {
			const serviceLocation = generateLocationFromProperty(serviceRequestProperty);

			return (
				<div onClick={() => onClickSidebarService(serviceRequest.service)}>
					<p className='uiServiceRequest__menu--name'>
						{`${serviceLocation?.address} ${serviceLocation?.unitNumber ? ` Unit ${serviceLocation.unitNumber}` : ''}`}
					</p>
					<p className=''>{serviceLocation?.alias}</p>
				</div>
			);
		}
	};

	const getMenuHeader = (serviceRequest) => {
		return (
			<div key={serviceRequest.id} className='uiServiceRequest__menu'>
				{renderAddress()}
				<div className='pl-separator' />
				<div className='uiServiceRequest__info'>
					{serviceRequest.type === EDIT_TYPE_SERVICE ? (
						<UIHover text={'edit type'}>
							<img src={EditPhotoIcon.white} alt={'service type'} className='uiServiceRequest__info--type' />
						</UIHover>
					) : (
						<UIHover text={'capture type'}>
							<img src={CameraWhite} alt={'service type'} className='uiServiceRequest__info--type' />
						</UIHover>
					)}
					<p className='uiServiceRequest__info--status'>{getReadableStatus(serviceRequest.status)}</p>
				</div>
				<div className='pl-separator' />
			</div>
		);
	};

	const renderSelectedTab = () => {
		const selectedTab = filterSelectedItem(getTabs(), location);
		switch (selectedTab) {
			case SERVICE_REQUEST_NOTES:
				return <ServiceRequestNotesTab serviceRequestId={serviceRequest.id} />;
			case SERVICE_REQUEST_EDITOR:
				return <ServiceRequestEditorTab serviceRequestId={serviceRequest.id} />;
			case SERVICE_REQUEST_FIXED_FILES:
				return <ServiceRequestFixedFilesTab serviceRequestId={serviceRequest.id} />;
			case SERVICE_REQUEST_INCORRECT:
				return <ServiceRequestIncorrectTab serviceRequestId={serviceRequest.id} />;
			default:
				return null;
		}
	};

	const renderRemoveModal = () => {
		const hideModal = () => setShowRemoveRequestModal(false);

		return (
			<UIModal condition={showRemoveRequestModal}>
				<UIOverlayCard hideModal={hideModal} title={strings.deleteServiceRequestTitle} description={strings.deleteServiceRequestDescription}>
					<UIIconButton
						isLoading={isRemovingServiceRequest}
						type={UI_ICON_BUTTON_CANCEL_TYPE}
						icon={Trashcan.white}
						text={strings.deleteServiceRequestButton}
						onClick={deleteServiceRequest}
					/>
				</UIOverlayCard>
			</UIModal>
		);
	};

	const renderApproveServiceRequestModal = () => {
		const hideModal = () => setShowApproveServiceRequestModal(false);

		return (
			<UIModal condition={showApproveServiceRequestModal}>
				<UIOverlayCard cardStyle={{ height: '100%' }} hideModal={hideModal} title={strings.approveTitle}>
					<div className='approveModal-title'>{strings.approveSubTitle}</div>
					<div className='approveModal'>
						{serviceRequest.requestedFiles &&
							serviceRequest.requestedFiles.map((requestedFile) => (
								<div className='approveModal__row' key={requestedFile.id}>
									<img key={requestedFile.id} className='approveModal__row--thumbnail' alt={requestedFile.filename} src={requestedFile['webUrl']} />
									<p className='approveModal__row--status'>Previous status: {requestedFile.status}</p>
								</div>
							))}
					</div>
					<div className='approveModal--container'>
						<div className='approveModal--container-title'>{strings.selectFileTitle}</div>
						<UILabeledInput
							type={UI_LABELED_INPUT_DROPDOWN_TYPE}
							options={FILE_STATUS_OPTIONS}
							containerStyle={{ margin: '32px 0 8px 0' }}
							onChangeDropdownValue={(completedFileStatus) => setCompletedFileStatus(completedFileStatus.value)}
							placeholder={strings.selectStatusPlaceholder}
						/>
						<div className='general-buttons-container'>
							<UIIconButton dark isDisabled={!completedFileStatus} isLoading={isApproving} onClick={approveServiceRequest} text={strings.approveButton} />
						</div>
					</div>
				</UIOverlayCard>
			</UIModal>
		);
	};

	const renderRequestStatusModal = () => {
		const hideModal = () => setShowRequestStatusModal(false);
		let status = Object.values(SERVICE_REQUESTS.status);
		let updatedStatus: DropdownItem[] = [];
		for (let i = 0; i < status.length; i++) {
			updatedStatus.push({ label: status[i], value: status[i] });
		}

		return (
			<UIModal condition={showRequestStatusModal}>
				<UIOverlayCard hideModal={hideModal} title={strings.changeStatusTitle}>
					<div className='serviceRequestStatusModal'>
						<UILabeledInput
							key={serviceRequest.id}
							label={strings.changeStatusLabel}
							type={UI_LABELED_INPUT_DROPDOWN_TYPE}
							options={updatedStatus}
							onChangeDropdownValue={(item) => serviceRequestStatusChange(item.value)}
							placeholder={strings.changeStatusPlaceholder}
						/>
					</div>
				</UIOverlayCard>
			</UIModal>
		);
	};

	const renderDownloadModal = () => {
		const { service } = serviceRequest;
		const hideModal = () => setShowDownloadModal(false);

		const downloadSelectedFilesByType = async (type: string) => {
			// old dl links for ref until raws are fixed (Brian is looking into it)
			//https://dev-portal-api.pineapplelabs.io/file-transfer/download?serviceRequestId=bf44ba72-11bc-4d36-a26e-52df66734def&size=original&includeRequestedFiles=true

			//raws : https://dev-portal-api.pineapplelabs.io/file-transfer/download?serviceId=9a3f3f00-d116-49f2-9b36-c6a82c3c48aa&size=original&includeRawPhotos=true

			let filesArr: string[] = [];
			let URL, selectedFiles;
			let baseUrl = getApiUrl(true);
			selectedFiles = serviceRequest.requestedFiles;
			if (selectedFiles) {
				for (let i = 0; i < selectedFiles.length; i++) {
					let file = `"${selectedFiles[i].id}"`;
					filesArr.push(file);
				}
				type === FILE_TYPE_RAW_IMAGE
					? (URL = `${baseUrl}/file-transfer/download?serviceId=` + service?.id + `&size=original?&includeRawPhotos=true`) //raws
					: (URL = `${baseUrl}/file-transfer/download-by-ids?&fileIds=[` + filesArr + ']&size=original?&includeRequestedFiles=true'); // incorrect
				window.open(URL, '_blank');
			}
		};

		return (
			<UIModal condition={showDownloadModal}>
				<UIOverlayCard hideModal={hideModal} title={strings.downloadModalTitle} description={strings.downloadModalDescription}>
					<div className='uiServiceRequest__buttons'>
						<div onClick={() => downloadSelectedFilesByType('incorrect')}>
							<UIIconButton
								className='uiServiceRequest__top-pin--item-button'
								iconPadding={2}
								type={UI_ICON_BUTTON_OUTLINE_TYPE}
								text={strings.downloadModalIncorrectButton}
								icon={DownloadIcon.white}
							/>
						</div>
						{service && (
							<div onClick={() => downloadSelectedFilesByType(FILE_TYPE_RAW_IMAGE)}>
								<UIIconButton
									className='uiServiceRequest__top-pin--item-button'
									iconPadding={2}
									type={UI_ICON_BUTTON_OUTLINE_TYPE}
									text={strings.downloadModalRawsButton}
									icon={DownloadIcon.white}
								/>
							</div>
						)}
					</div>
				</UIOverlayCard>
			</UIModal>
		);
	};

	if (!serviceRequest) {
		return <UILoadingPage />;
	}

	return (
		<div className={`uiController`}>
			<UIMenuBody menuHeader={getMenuHeader(serviceRequest)} menuItems={getTabs()}>
				<>
					<div className='uiServiceRequest__top-pin'>
						<div className='uiServiceRequest__top-pin--item'>
							<UIIconButton
								dark
								icon={DownloadBlack}
								iconAlt='download button'
								className='uiServiceRequest__download'
								onClick={() => setShowDownloadModal(true)}
							/>
						</div>
						{serviceRequest.status === SERVICE_REQUESTS.status.inProgress && (
							<div className='uiServiceRequest__top-pin--item'>
								<UIIconButton
									dark
									className='uiServiceRequest__top-pin--item-button'
									isDisabled={!serviceRequest['completedFiles'] || serviceRequest['completedFiles'].length === 0}
									text={strings.sendForApprovalButton}
									isLoading={isSendingForApproval}
									disabledErrorMessage={strings.disabledSendForApprovalMessage}
									onClick={sendForApproval}
								/>
							</div>
						)}
						{serviceRequest.status === SERVICE_REQUESTS.status.awaitingAdminApproval && isUserAdmin && (
							<div className='uiServiceRequest__top-pin--item'>
								<UIIconButton
									dark
									className='uiServiceRequest__top-pin--item-button'
									isDisabled={!serviceRequest['completedFiles'] || serviceRequest['completedFiles'].length === 0}
									text={strings.approveButton}
									disabledErrorMessage={strings.disabledSendForApprovalMessageAlt}
									onClick={() => setShowApproveServiceRequestModal(true)}
								/>
							</div>
						)}
						{serviceRequest.status === SERVICE_REQUESTS.status.awaitingRequestApproval && isUserAdmin && (
							<div className='uiServiceRequest__top-pin--item'>
								<UIIconButton
									dark
									className='uiServiceRequest__top-pin--item-button'
									isLoading={isSendingToEditor}
									isDisabled={!serviceRequest.editor}
									text={strings.sendToEditorButton}
									disabledErrorMessage={strings.disabledSendToEditorButton}
									onClick={sendServiceRequestToEditor}
								/>
							</div>
						)}
						{isUserAdmin && (
							<div className='uiServiceRequest__top-pin--item-admin'>
								<ContextMenuTrigger id='service-requests-admin-options' holdToDisplay={0}>
									<UIIconButton iconAlt={strings.settingsButtonAlt} type={UI_ICON_BUTTON_OUTLINE_TYPE} icon={SettingsWhite} />
								</ContextMenuTrigger>
								<ContextMenu className='plContextMenu' id='service-requests-admin-options'>
									<div className='adminOptionsMenu__row' onClick={() => setShowRemoveRequestModal(true)}>
										<UIHover text={strings.removeHover}>
											<UIIcon className='uiServiceRequest__header--delete' iconSrc={Trashcan.white} alt={'remove'} />
										</UIHover>
										<p className='adminOptionsMenu__row--name'>Delete</p>
									</div>
									<div className='adminOptionsMenu__row' onClick={() => setShowRequestStatusModal(true)}>
										<UIHover text={strings.changeStageHover}>
											<UIIcon className='uiServiceRequest__header--default' iconSrc={StageIcon.white} alt={'stage'} />
										</UIHover>
										<p className='adminOptionsMenu__row--name'>Change Status</p>
									</div>
								</ContextMenu>
							</div>
						)}
					</div>
					<div className='uiServiceRequest__content'>{renderSelectedTab()}</div>
					{renderRemoveModal()}
					{renderApproveServiceRequestModal()}
					{renderRequestStatusModal()}
					{renderDownloadModal()}
				</>
			</UIMenuBody>
		</div>
	);
}
